import { useLayoutEffect, useMemo, useRef } from 'react'

/**
 * Callbacks with a stable identity that always call the newest closure
 * (1.155.0, review D4).
 *
 * `Bubble` is `memo`, and the chat page hands every bubble a dozen
 * callbacks that close over the page's state (whether there is a net,
 * which selection is open, the waiting move). Written inline, each is a
 * new function on every drawing, and `memo` would see new props on every
 * tick and draw every row anyway; written once with `useCallback` they
 * would call a stale closure. So: one wrapper per name, made once, that
 * calls whatever the page handed in last. The keys must be the same on
 * every call -- they are the names of the callbacks, not data.
 *
 * The newest set is taken in a layout effect, not during the render: an
 * event handler runs after the commit, so it sees the closure of the
 * drawing it belongs to, and nothing is written to a ref while React draws.
 */
export function useStableCallbacks<T extends Record<string, (...args: any[]) => unknown>>(handlers: T): T {
  const latest = useRef(handlers)
  useLayoutEffect(() => {
    latest.current = handlers
  })
  return useMemo(() => {
    const stable = {} as Record<string, (...args: unknown[]) => unknown>
    for (const key of Object.keys(handlers)) {
      stable[key] = (...args: unknown[]) => latest.current[key]!(...args)
    }
    return stable as T
    // Once: the keys never change, and the wrappers must not either.
  }, [])
}
